import {useCallback, useEffect, useMemo, useState} from "react";
import {PhoneHQIconAvatar, PhoneHQAvatar, PhoneHQButton, PhoneHQCheckbox, PhoneHQDatepicker} from 'ch3-ui-lib';
import {debounce} from "lodash";
import {useSelector} from "react-redux";
import {selectUserId} from "../../store/features/Auth/authSlice";
import {User, useSearchUsersQuery} from "../../store/features/Users/userService";
import {
    FetchGroupsResponse,
    useFetchGroupsQuery,
} from "../../store/features/groups/groupsService";
import {Colors} from "../../utilis/types/Types";
import {getFullName} from "../../utilis/getFullName";
import {Calendar, ChevronDown, ChevronUp, Cloudy, Folder, Trash2, X} from "lucide-react";
import {nextGroupsCursor, SharedSearchField} from "./AccessSelector";

export interface ContactFilters {
    sharedForUsers: number[];
    sharedForGroups: number[];
    dateFrom?: string;
    dateTo?: string;
    sources?: string[];
    /** Display names for the active-filter chips. */
    sharedForUserNames?: string[];
    sharedForGroupNames?: string[];
    dateLabel?: string;
}

interface SelectedUser {
    userId: number;
    firstName: string;
    lastName: string;
    avatarUrl?: string;
    iconColor?: string;
}

interface SelectedGroup {
    groupId: number;
    name: string;
    icon?: string;
    iconColor?: string;
    memberCount?: number;
}

interface ContactsFilterPanelProps {
    open: boolean;
    onClose: () => void;
    onApply: (filters: ContactFilters) => void;
    initialFilters?: ContactFilters;
    sourceCounts?: Record<string, number>;
}

const GROUPS_OWNER = "contacts-filter";

const SOURCE_OPTIONS: {label: string; value: string}[] = [
    {label: "System", value: "SYSTEM"},
    {label: "Pipedrive", value: "PIPEDRIVE"},
    {label: "SIM", value: "DEVICE"},
];

export default function ContactsFilterPanel({open, onClose, onApply, initialFilters, sourceCounts}: ContactsFilterPanelProps) {
    const userId = useSelector(selectUserId);

    // Accordion state
    const [expandedSection, setExpandedSection] = useState<string | null>(null);

    // Shared for filter state
    const [searchPhrase, setSearchPhrase] = useState("");
    const [phrase, setPhrase] = useState("");
    const [groupsQuery, setGroupsQuery] = useState<any>({});
    const [selectedUsers, setSelectedUsers] = useState<SelectedUser[]>([]);
    const [selectedGroups, setSelectedGroups] = useState<SelectedGroup[]>([]);
    const [dropdownTab, setDropdownTab] = useState<"USERS" | "GROUPS">("USERS");

    // Source filter state (multi-select)
    const [selectedSources, setSelectedSources] = useState<string[]>([]);

    // Date filter state
    const [dateRange, setDateRange] = useState<{from: string; to: string} | null>(null);
    const [dateLabel, setDateLabel] = useState("All time");
    const [datepickerKey, setDatepickerKey] = useState(0);

    const labelToPresetValue = (label: string): string => {
        switch (label) {
            case "Today": return "today";
            case "Yesterday": return "yesterday";
            case "This week": return "this-week";
            case "Last week": return "last-week";
            case "This month": return "this-month";
            case "Last month": return "last-month";
            case "This year": return "this-year";
            case "Last year": return "last-year";
            case "All time": return "all-time";
            default: return "";
        }
    };

    useEffect(() => {
        setGroupsQuery({});
    }, [open]);

    useEffect(() => {
        if (!open) return;
        setSelectedSources(initialFilters?.sources ?? []);
        if (initialFilters?.dateFrom && initialFilters?.dateTo) {
            setDateRange({from: initialFilters.dateFrom, to: initialFilters.dateTo});
            const formatDate = (iso: string) =>
                new Date(iso).toLocaleDateString("en-US", {month: "short", day: "numeric", year: "numeric"});
            setDateLabel(`${formatDate(initialFilters.dateFrom)} - ${formatDate(initialFilters.dateTo)}`);
        } else {
            setDateRange(null);
            setDateLabel("All time");
        }
        setDatepickerKey(k => k + 1);
    }, [open, initialFilters]);

    const {data: usersData} = useSearchUsersQuery(phrase);
    const {data: groupData} = useFetchGroupsQuery({phrase, userId, owner: GROUPS_OWNER, ...groupsQuery});

    const groups = useMemo(() =>
            (groupData?.groups ?? []).map((g: FetchGroupsResponse) => ({
                groupId: g.group.groupId,
                name: g.group.name,
                icon: g.group.icon,
                iconColor: g.group.iconColor,
                memberCount: g.users?.length,
                users: g.users,
            })),
        [groupData]
    );

    const debouncedSetPhrase = useCallback(
        debounce((val: string) => {
            setGroupsQuery({});
            setPhrase(val);
        }, 500),
        []
    );

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        setSearchPhrase(val);
        debouncedSetPhrase(val);
    };

    const handleGroupsScrollEnd = () => {
        const cursor = nextGroupsCursor(groupData);
        if (cursor) setGroupsQuery(cursor);
    };

    const filteredUsers = usersData?.filter(
        (u: User) => !selectedUsers.find(su => su.userId === u.userId)
    ) ?? [];

    const filteredGroups = groups.filter(
        (g: any) => !selectedGroups.find(sg => sg.groupId === g.groupId)
    );

    const handleSelectUser = (user: User) => {
        setSelectedUsers(prev => [...prev, {
            userId: user.userId,
            firstName: user.firstName,
            lastName: user.lastName,
            avatarUrl: user.avatarUrl,
            iconColor: user.iconColor as string | undefined,
        }]);
        setSearchPhrase("");
        setGroupsQuery({});
        setPhrase("");
    };

    const handleSelectGroup = (group: any) => {
        setSelectedGroups(prev => [...prev, {
            groupId: group.groupId,
            name: group.name,
            icon: group.icon,
            iconColor: group.iconColor,
            memberCount: group.users?.length,
        }]);
        setSearchPhrase("");
        setGroupsQuery({});
        setPhrase("");
    };

    const handleRemoveUser = (userId: number) => {
        setSelectedUsers(prev => prev.filter(u => u.userId !== userId));
    };

    const handleRemoveGroup = (groupId: number) => {
        setSelectedGroups(prev => prev.filter(g => g.groupId !== groupId));
    };

    const handleResetSharedFor = () => {
        setSelectedUsers([]);
        setSelectedGroups([]);
    };

    const handleResetSource = () => {
        setSelectedSources([]);
    };

    const toggleSource = (value: string) => {
        setSelectedSources(prev =>
            prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
        );
    };

    const handleResetDate = () => {
        setDateRange(null);
        setDateLabel("All time");
        setDatepickerKey(k => k + 1);
    };

    const toggleSection = (section: string) => {
        setExpandedSection(prev => prev === section ? null : section);
    };

    const handleDateConfirm = (dates: any[], presetLabel?: string) => {
        if (presetLabel === "All time") {
            setDateRange(null);
            setDateLabel("All time");
            return;
        }
        if (dates && dates.length === 2) {
            const from = dates[0].toDate ? dates[0].toDate().toISOString() : new Date(dates[0]).toISOString();
            const to = dates[1].toDate ? dates[1].toDate().toISOString() : new Date(dates[1]).toISOString();
            setDateRange({from, to});

            if (presetLabel) {
                setDateLabel(presetLabel);
            } else {
                const fromDate = dates[0].toDate ? dates[0].toDate() : new Date(dates[0]);
                const toDate = dates[1].toDate ? dates[1].toDate() : new Date(dates[1]);
                const formatDate = (d: Date) =>
                    d.toLocaleDateString("en-US", {month: "short", day: "numeric", year: "numeric"});
                setDateLabel(`${formatDate(fromDate)} - ${formatDate(toDate)}`);
            }
        }
    };

    const handleApply = () => {
        const filters: ContactFilters = {
            sharedForUsers: selectedUsers.map(u => u.userId),
            sharedForGroups: selectedGroups.map(g => g.groupId),
            sharedForUserNames: selectedUsers.map(u => `${u.firstName} ${u.lastName}`.trim()),
            sharedForGroupNames: selectedGroups.map(g => g.name),
            sources: selectedSources.length > 0 ? selectedSources : undefined,
            dateFrom: dateRange?.from,
            dateTo: dateRange?.to,
            dateLabel: dateRange ? dateLabel || undefined : undefined,
        };
        onApply(filters);
        onClose();
    };

    const handleCancel = () => {
        onClose();
    };

    const hasSharedForSelection = selectedUsers.length > 0 || selectedGroups.length > 0;
    const hasSourceSelection = selectedSources.length > 0;
    const hasDateSelection = dateRange !== null;

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 flex justify-end">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-scrim" onClick={onClose}/>

            {/* Panel */}
            <div className="relative bg-surface rounded-l-[20px] w-[640px] max-w-[90vw] flex flex-col shadow-overlay overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between border-b border-line pl-5 pr-3 py-3.5">
                    <span className="text-sm font-semibold text-ink">Filters</span>
                    <button
                        onClick={onClose}
                        className="flex items-center justify-center size-7 rounded-control hover:bg-surface-muted cursor-pointer"
                    >
                        <X size={18} className="!p-0 text-ink-muted"/>
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto">
                    {/* Shared for section */}
                    <div className="border-b border-line">
                        <div
                            className="flex items-center gap-4 pl-5 pr-3 py-3.5 cursor-pointer"
                            onClick={() => toggleSection("shared-for")}
                        >
                            <div className="bg-surface-muted rounded-control size-9 flex items-center justify-center shrink-0">
                                <Folder size={18} className="text-ink-muted"/>
                            </div>
                            <span className="flex-1 text-sm font-medium text-ink truncate">Shared for</span>
                            <div className="flex items-center gap-2">
                                {hasSharedForSelection && (
                                    <PhoneHQButton
                                        variant="secondary"
                                        size="sm"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleResetSharedFor();
                                        }}
                                    >
                                        Reset
                                    </PhoneHQButton>
                                )}
                                <div className="flex items-center justify-center size-9 rounded-control">
                                    {expandedSection === "shared-for"
                                        ? <ChevronUp size={20} className="text-ink-muted"/>
                                        : <ChevronDown size={20} className="text-ink-muted"/>
                                    }
                                </div>
                            </div>
                        </div>

                        {expandedSection === "shared-for" && (
                            <div className="pl-[68px] pr-5 pb-5">
                                <SharedSearchField
                                    value={searchPhrase}
                                    onChange={handleSearchChange}
                                    hasItems={filteredUsers.length > 0 || filteredGroups.length > 0}
                                    activeTab={dropdownTab}
                                    onTabChange={setDropdownTab}
                                    users={filteredUsers}
                                    groups={filteredGroups}
                                    onSelectUser={handleSelectUser}
                                    onSelectGroup={handleSelectGroup}
                                    onGroupsScrollEnd={handleGroupsScrollEnd}
                                />

                                {/* Selected items */}
                                {(selectedGroups.length > 0 || selectedUsers.length > 0) && (
                                    <div className="flex flex-col">
                                        {selectedGroups.map(group => (
                                            <div
                                                key={`sg-${group.groupId}`}
                                                className="flex items-center gap-3 py-4 border-b border-line"
                                            >
                                                <PhoneHQIconAvatar icon={group.icon} color={group.iconColor || Colors.grey} name={group.name} size="24px"/>
                                                <div className="flex flex-col flex-1 min-w-0">
                                                    <span className="text-footnote font-medium text-ink truncate">{group.name}</span>
                                                    {group.memberCount !== undefined && (
                                                        <span className="text-footnote text-ink-muted">{group.memberCount} members</span>
                                                    )}
                                                </div>
                                                <button
                                                    onClick={() => handleRemoveGroup(group.groupId)}
                                                    className="flex items-center justify-center size-7 rounded-control hover:bg-surface-muted cursor-pointer"
                                                >
                                                    <Trash2 size={18} className="!p-0 !text-ink-muted"/>
                                                </button>
                                            </div>
                                        ))}
                                        {selectedUsers.map(user => (
                                            <div
                                                key={`su-${user.userId}`}
                                                className="flex items-center gap-3 py-4 border-b border-line last:border-b-0"
                                            >
                                                <PhoneHQAvatar
                                                    size="24px"
                                                    type={user.avatarUrl ? "image" : "initials"}
                                                    src={user.avatarUrl}
                                                    name={getFullName(user.firstName, user.lastName)}
                                                    colorFromName
                                                />
                                                <span className="flex-1 text-footnote font-medium text-ink truncate">
                                                    {getFullName(user.firstName, user.lastName)}
                                                </span>
                                                <button
                                                    onClick={() => handleRemoveUser(user.userId)}
                                                    className="flex items-center justify-center size-7  rounded-control hover:bg-surface-muted cursor-pointer"
                                                >
                                                    <Trash2 size={18} className="!p-0 !text-ink-muted"/>
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Source section */}
                    <div className="border-b border-line">
                        <div
                            className="flex items-center gap-4 pl-5 pr-3 py-3.5 cursor-pointer"
                            onClick={() => toggleSection("source")}
                        >
                            <div className="bg-surface-muted rounded-control size-9 flex items-center justify-center shrink-0">
                                <Cloudy size={18} className="text-ink-muted"/>
                            </div>
                            <span className="flex-1 text-sm font-medium text-ink truncate">Source</span>
                            <div className="flex items-center gap-2">
                                {hasSourceSelection && (
                                    <PhoneHQButton
                                        variant="secondary"
                                        size="sm"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleResetSource();
                                        }}
                                    >
                                        Reset
                                    </PhoneHQButton>
                                )}
                                <div className="flex items-center justify-center size-9 rounded-control">
                                    {expandedSection === "source"
                                        ? <ChevronUp size={20} className="text-ink-muted"/>
                                        : <ChevronDown size={20} className="text-ink-muted"/>
                                    }
                                </div>
                            </div>
                        </div>

                        {expandedSection === "source" && (
                            <div className="pl-[68px] pr-7 pb-5">
                                <div className="flex flex-col gap-5">
                                    {SOURCE_OPTIONS.map(option => (
                                        <label
                                            key={option.value}
                                            className="flex items-center gap-[10px] cursor-pointer"
                                        >
                                            <PhoneHQCheckbox
                                                value={option.value}
                                                checked={selectedSources.includes(option.value)}
                                                onCheckedChange={() => toggleSource(option.value)}
                                            />
                                            <span className="flex-1 text-footnote font-medium leading-4 text-ink truncate">
                                                {option.label}
                                            </span>
                                            {typeof sourceCounts?.[option.value] === "number" && (
                                                <span className="text-footnote font-medium leading-4 text-ink-muted">
                                                    {sourceCounts[option.value]}
                                                </span>
                                            )}
                                        </label>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Date created section */}
                    <div className="border-b border-line">
                        <div
                            className="flex items-center gap-4 pl-5 pr-3 py-3.5 cursor-pointer"
                            onClick={() => toggleSection("date-created")}
                        >
                            <div className="bg-surface-muted rounded-control size-9 flex items-center justify-center shrink-0">
                                <Calendar size={18} className="text-ink-muted"/>
                            </div>
                            <span className="flex-1 text-sm font-medium text-ink truncate">Date created</span>
                            <div className="flex items-center gap-2">
                                {hasDateSelection && (
                                    <PhoneHQButton
                                        variant="secondary"
                                        size="sm"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleResetDate();
                                        }}
                                    >
                                        Reset
                                    </PhoneHQButton>
                                )}
                                <div className="flex items-center justify-center size-9 rounded-control">
                                    {expandedSection === "date-created"
                                        ? <ChevronUp size={20} className="text-ink-muted"/>
                                        : <ChevronDown size={20} className="text-ink-muted"/>
                                    }
                                </div>
                            </div>
                        </div>

                        {expandedSection === "date-created" && (
                            <div className="pl-[68px] pr-5 pb-5">
                                <PhoneHQDatepicker
                                    key={datepickerKey}
                                    defaultOption={labelToPresetValue(dateLabel)}
                                    defaultDates={dateRange ? [dateRange.from, dateRange.to] : undefined}
                                    onDateConfirm={handleDateConfirm}
                                    trigger={
                                        <button className="flex items-center gap-2 w-[280px] h-[36px] px-3 border border-fill-subtle rounded-control text-footnote font-medium text-ink hover:border-brand cursor-pointer bg-surface">
                                            <Calendar size={16} className="!p-0 text-ink-muted"/>
                                            <span className="flex-1 text-left truncate">{dateLabel}</span>
                                        </button>
                                    }
                                />
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 px-5 py-3.5 border-t border-line bg-canvas">
                    <PhoneHQButton variant="secondary" onClick={handleCancel}>
                        Cancel
                    </PhoneHQButton>
                    <PhoneHQButton variant="primary" onClick={handleApply}>
                        Apply
                    </PhoneHQButton>
                </div>
            </div>
        </div>
    );
}
